import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import "./PostPurchasePage.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useCartStore } from "../../../../zustand/cartStore";
import { useAuthStore } from "../../../../zustand/loginStore";
import CustomNavbarClients from "../navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import Footer from "../Footer";
import OrderSummary from "../sellSection/OrderSummary";
import ImageModal from "components/general/ImageModal";
import {
  createAuthenticatedAxiosInstance,
  getClientOrderDetails,
} from "api/auth";
import LoadingScreen from "components/general/LoadingScreen";
import { useBreakpoints } from "hooks/useBreakPoints";

/**
 * Interface representing a processed product for display in the post-purchase page
 */
interface ProcessedProduct {
  size_product_id: number;
  product_name: string;
  color: string;
  size_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  image: string;
}

/**
 * Post-purchase page component that displays order confirmation and details
 * Shows success message, order summary, and purchased items details
 * @returns {JSX.Element} The post-purchase page component
 */
const PostPurchasePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCartStore();
  const { token } = useAuthStore();
  const { isMobile } = useBreakpoints();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [orderProducts, setOrderProducts] = useState<ProcessedProduct[]>([]);
  const [allImages, setAllImages] = useState<string[]>([]);

  const orderState = location.state?.orderDetails || {};
  const [orderId, setOrderId] = useState(orderState.orderId || "");

  /**
   * Effect hook to fetch order details and clear cart
   * Runs when orderId, token, or clearCart dependencies change
   * Fetches order details from API and processes product information for display
   */
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setIsLoading(false);
        return;
      }

      try {
        const axiosInstance = createAuthenticatedAxiosInstance(token);
        const data = await getClientOrderDetails(axiosInstance, orderId);

        setOrderDetails(data);

        if (data && data.order_products && Array.isArray(data.order_products)) {
          const products: ProcessedProduct[] = [];
          const images: string[] = [];

          data.order_products.forEach((orderProduct: any, index: number) => {
            const unitPrice =
              parseFloat(orderProduct.subtotal) / orderProduct.quantity;

            const processedProduct: ProcessedProduct = {
              size_product_id: orderProduct.size_product_id,
              product_name: orderProduct.product_name,
              color: orderProduct.color,
              size_name: orderProduct.size,
              quantity: orderProduct.quantity,
              unit_price: unitPrice,
              total_price: parseFloat(orderProduct.subtotal),
              image: orderProduct.image || "",
            };

            products.push(processedProduct);

            if (orderProduct.image) {
              images.push(orderProduct.image);
            }
          });

          setOrderProducts(products);
          setAllImages(images);
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderDetails();

    const timer = setTimeout(() => {
      clearCart();
    }, 1000);

    return () => clearTimeout(timer);
  }, [orderId, token, clearCart]);

  /**
   * Handles clicking on product images to open image modal
   * @param {number} index - The index of the image to display in the modal
   */
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };

  /**
   * Handles closing the image modal
   */
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
  };

  /**
   * Handles selecting a different image in the modal
   * @param {number} index - The index of the image to select
   */
  const handleSelectImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  /**
   * Calculates order totals including subtotal, total items, shipping cost, and total cost
   * Uses order details from backend if available, otherwise calculates from order products
   * @returns {Object} Object containing subtotal, totalItems, shippingCost, and totalCost
   */
  const calculateTotals = () => {
    if (orderDetails) {
      const orderTotal = parseFloat(orderDetails.total);
      const subtotal = orderProducts.reduce(
        (sum, item) => sum + item.total_price,
        0
      );
      const totalItems = orderProducts.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const shippingCost = orderTotal - subtotal;

      return {
        subtotal,
        totalItems,
        shippingCost: Math.max(0, shippingCost),
        totalCost: orderTotal,
      };
    }

    if (!orderProducts.length)
      return { subtotal: 0, totalItems: 0, shippingCost: 0, totalCost: 0 };

    const subtotal = orderProducts.reduce(
      (sum, item) => sum + item.total_price,
      0
    );
    const totalItems = orderProducts.reduce(
      (sum, item) => sum + item.quantity,
      0
    );
    const shippingCost = orderProducts.length > 0 ? 50 : 0;
    const totalCost = subtotal + shippingCost;

    return { subtotal, totalItems, shippingCost, totalCost };
  };

  const { subtotal, totalItems, shippingCost, totalCost } = calculateTotals();

  if (isLoading) {
    return <LoadingScreen loadingText="Cargando detalles de la orden..." />;
  }

  return (
    <>
      <CustomNavbarClients />
      <ClientMenuBar />

      <Container className="post-purchase-container">
        {/* Success message centered without box */}
        <div className="centered-success-message">
          <h1 className="success-title">¡Compra realizada!</h1>
          <div className="success-icon-wrapper">
            <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
          </div>
          <h1 className="success-title">¡Gracias por realizar tu pedido!</h1>
          <p className="success-text">
            En preparación del pedido comenzará cuando se confirme la
            transferencia.
          </p>
          <p className="success-text">
            Recibirás un correo con las actualizaciones de tu compra.
          </p>

          <div className="action-links">
            <a
              href="/orders"
              className="action-link primary"
              onClick={(e) => {
                e.preventDefault();
                navigate("/profile/orders");
              }}
            >
              Ver en mis pedidos
            </a>
            <a
              href="/"
              className="action-link secondary"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              Seguir comprando
            </a>
          </div>
        </div>

        {/* Content area with purchase summary and order summary side by side */}
        <Row className="mt-5 purchase-content-row">
          {/* Order Summary - aparece arriba en móvil */}
          {isMobile && (
            <Col lg={4} md={12}>
              <div className="order-summary-container">
                <OrderSummary
                  totalItems={totalItems}
                  subtotal={subtotal}
                  shippingCost={shippingCost}
                  totalCost={totalCost}
                  buttonDisabled={true}
                  showButton={false}
                  showShippingCost={true}
                />
              </div>
            </Col>
          )}

          <Col lg={8} md={12}>
            <div className="purchase-details-section">
              <h2 className="summary-title">Resumen de la compra</h2>
              <div className="products-list">
                {orderProducts.length > 0 ? (
                  orderProducts.map((product, index) => (
                    <div
                      key={`${product.size_product_id}-${index}`}
                      className="product-item"
                    >
                      <Row className="align-items-center">
                        {/* Imagen del producto */}
                        <Col xs={3} md={2} className="product-image-container">
                          <div className="product-image-wrapper">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.product_name}
                                className="product-image"
                                onClick={() => handleImageClick(index)}
                                style={{ cursor: "pointer" }}
                              />
                            ) : (
                              <div className="no-image-placeholder">
                                Sin imagen
                              </div>
                            )}
                          </div>
                        </Col>

                        {/* Detalles del producto */}
                        <Col xs={6} md={7} className="product-details">
                          <h5 className="product-name">
                            {product.product_name}
                          </h5>
                          <div className="product-attributes">
                            <p className="product-attribute">
                              <span className="attribute-label">Talla:</span>{" "}
                              {product.size_name}
                            </p>
                            <p className="product-attribute">
                              <span className="attribute-label">Color:</span>{" "}
                              {product.color}
                            </p>
                            <p className="product-attribute">
                              <span className="attribute-label">Cantidad:</span>{" "}
                              {product.quantity}
                            </p>
                          </div>
                        </Col>

                        {/* Precio */}
                        <Col xs={3} md={3} className="product-price-container">
                          <div className="product-price">
                            <p className="price-label">Precio:</p>
                            <p className="price-value">
                              $
                              {product.total_price.toLocaleString("es-MX", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                            {product.quantity > 1 && (
                              <p className="unit-price">
                                ($
                                {product.unit_price.toLocaleString("es-MX", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                c/u)
                              </p>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))
                ) : (
                  <div className="no-products-message">
                    <p>
                      No se encontraron detalles de los productos comprados.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Order Summary - aparece a la derecha en desktop */}
          {!isMobile && (
            <Col lg={4} md={12}>
              <div className="order-summary-container">
                <OrderSummary
                  totalItems={totalItems}
                  subtotal={subtotal}
                  shippingCost={shippingCost}
                  totalCost={totalCost}
                  buttonDisabled={true}
                  showButton={false}
                  showShippingCost={true}
                />
              </div>
            </Col>
          )}
        </Row>
      </Container>

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        images={allImages}
        selectedIndex={selectedImageIndex}
        onSelectImage={handleSelectImage}
        isReadOnly={true}
      />

      <Footer />
    </>
  );
};

export default PostPurchasePage;
